/**
 * All-time XP leaderboard — top players + the caller's own ranked row.
 * Handles are PII-free server-generated names (never emails). When the
 * caller isn't in the visible top rows, their row is appended below a
 * divider so "where you stand" is always answerable.
 */

import { useQuery } from "@tanstack/react-query";

import { getLeaderboard, type LeaderboardEntry } from "@/features/gamification/lib/gamification";

export function LeaderboardPanel() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: getLeaderboard,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-5">
        <p className="text-sm text-text-muted">Loading leaderboard…</p>
      </section>
    );
  }
  if (isError || !data) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-5">
        <p className="text-sm text-danger">Couldn't load the leaderboard.</p>
      </section>
    );
  }

  const youInTop = data.you
    ? data.entries.some((e) => e.isYou)
    : false;

  return (
    <section className="rounded-2xl border border-border bg-surface p-5 space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-tight text-text">
          Leaderboard
        </h2>
        <span className="text-xs text-text-muted">
          {data.you
            ? `You're #${data.you.rank} of ${data.totalPlayers}`
            : `${data.totalPlayers} ranked`}
        </span>
      </div>

      {data.entries.length === 0 ? (
        <p className="text-sm text-text-muted">
          No ranked players yet. Drill a spot to get on the board.
        </p>
      ) : (
        <div className="space-y-1">
          {data.entries.map((e) => (
            <Row key={e.rank} entry={e} />
          ))}
          {data.you && !youInTop && (
            <>
              <div className="flex justify-center py-1 text-text-muted/50 text-xs">
                · · ·
              </div>
              <Row entry={data.you} />
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Row({ entry }: { entry: LeaderboardEntry }) {
  return (
    <div
      className={[
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm",
        entry.isYou ? "bg-accent/15 border border-accent/30" : "bg-bg/30",
      ].join(" ")}
    >
      <span className="w-7 text-text-muted tabular-nums shrink-0">
        {entry.rank}
      </span>
      <span
        className={[
          "flex-1 min-w-0 truncate font-medium flex items-center gap-1.5",
          entry.isYou ? "text-accent" : "text-text",
        ].join(" ")}
      >
        <span className="truncate">{entry.handle}</span>
        {entry.isYou && <span className="text-[11px] text-accent/80 shrink-0">you</span>}
        <TierBadge tier={entry.tier} />
      </span>
      <span className="text-text-muted text-xs shrink-0">Lv {entry.level}</span>
      <span className="text-text tabular-nums shrink-0 w-16 text-right">
        {entry.totalXp.toLocaleString()}
      </span>
    </div>
  );
}

// License-tier badge — social proof. Paid tiers escalate in prominence
// (Unlimited gets the premium gold); Free stays muted but legible.
const TIER_STYLES: Record<string, string> = {
  Unlimited: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  Plus: "bg-accent/20 text-accent border-accent/40",
  Core: "bg-accent/15 text-accent border-accent/30",
  Free: "bg-text/10 text-text-muted border-border",
};

function TierBadge({ tier }: { tier: string }) {
  // Empty/missing (e.g. a leaderboard row cached before the tier field
  // shipped) falls back to Free rather than rendering a blank pill.
  const label = tier || "Free";
  return (
    <span
      className={[
        "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border leading-none",
        TIER_STYLES[label] ?? TIER_STYLES.Free,
      ].join(" ")}
    >
      {label}
    </span>
  );
}
